const io = require('socket.io-client');
const { isUndefined } = require('lodash');
const { getHeaders, getAxiosInstance, getSocketURL } = require('../connection');

let mySocket,
    token,
    keepOpen = false,
    reconnectInterval,
    connecting = false,
    presenterCode = null;

const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;
let reconnectAttempts = 0,
    reconnectTimer = null;

// Exponential backoff with jitter: the server drops sockets it rejects, and pages stay open for hours
const reconnect = () => {
    if (reconnectTimer) return;
    const delay = Math.min(RECONNECT_MAX_DELAY, RECONNECT_BASE_DELAY * 2 ** reconnectAttempts);
    reconnectAttempts++;
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (keepOpen && !socket().connected) {
            socket().close();
            voting.socket.connect();
        }
    }, delay * (0.5 + Math.random() / 2));
};

const resetReconnect = () => {
    reconnectAttempts = 0;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
};

const socket = function () {
    if (!mySocket) {
        mySocket = io(getSocketURL() + '/livevoting', {
            autoConnect: false,
            transports: ['websocket'],
            reconnection: false,
        });
        mySocket.on('connect', () => {
            let tokens,
                h = getHeaders();
            if (presenterCode) {
                tokens = { presenterCode };
            } else if (token) {
                tokens = { token };
            } else {
                tokens = h;
            }
            //console.log('Socket authentication using ', tokens);
            mySocket.emit('auth', tokens, () => {
                // console.log('   authenticated');
            });
            clearInterval(reconnectInterval);
            reconnectInterval = false;
        });
        // TODO: add some kind of debounce here?
        mySocket.on('disconnect', () => {
            //console.log('disconnected');
            token = null;
            reconnect();
        });
    }
    return mySocket;
};

const fetchTempToken = () => {
    return getAxiosInstance({
        method: 'GET',
        url: '/livevoting/token',
    }).then((r) => r.data);
};

const voting = (module.exports = {
    socket: {
        setPresenterCode: (code) => {
            presenterCode = code;
        },
        connect: () => {
            if (connecting) return;
            connecting = true;
            keepOpen = true;
            if (!reconnectInterval) {
                reconnectInterval = setInterval(reconnect, 10000);
            }
            return new Promise((resolve, reject) => {
                if (!socket().connected) {
                    if (getHeaders()['X-CSRFToken']) {
                        fetchTempToken()
                            .then((response) => {
                                token = response.token;
                            })
                            .then(resolve)
                            .catch(reject);
                    } else {
                        return resolve();
                    }
                } else {
                    resolve();
                }
            })
                .then(() => {
                    if (!socket().connected) {
                        socket().open();
                    }
                })
                .catch((err) => {
                    // fail
                    //console.log(err);
                    token = null;
                })
                .then(() => {
                    connecting = false;
                });
        },
        disconnect: () => {
            token = null;
            keepOpen = false;
            resetReconnect();
            clearInterval(reconnectInterval);
            reconnectInterval = false;
            socket().close();
        },
        getOpenSessions: (eventId) =>
            new Promise((resolve, reject) => {
                socket().emit('get_open_sessions', eventId);
            }),
        joinVotingSession: (liveVotingId) =>
            new Promise((resolve, reject) => {
                socket().emit('join_voting_session', liveVotingId, (response) => {
                    // A rejection is acked as [false, reason]
                    if (typeof response === 'string') {
                        resetReconnect();
                        return resolve(JSON.parse(response));
                    } else {
                        reject(new Error(Array.isArray(response) ? response[1] : 'join failed'));
                    }
                });
            }),
        joinAdminRoom: (liveVotingId) =>
            new Promise((resolve, reject) => {
                socket().emit('join_admin_room', liveVotingId, (response) => {
                    if (response == 'success') {
                        resolve();
                    } else {
                        //console.log(response);
                        return reject();
                    }
                });
            }),
        setActiveScreen: (liveVotingId, question, resultsVisible, votingEnabled) =>
            new Promise((resolve, reject) =>
                socket().emit(
                    'set_active_screen',
                    liveVotingId,
                    {
                        id: question.id,
                        text: question.text,
                        type: question.answer_type,
                        description: question.description,
                        options: question.options,
                    },
                    !!resultsVisible,
                    !!votingEnabled,
                    resolve
                )
            ),
        setVotingEnabled: (liveVotingId, votingEnabled) =>
            new Promise((resolve, reject) => {
                socket().emit('set_voting_enabled', liveVotingId, !!votingEnabled, resolve);
            }),
        setResultsVisibility: (liveVotingId, resultsVisible) =>
            new Promise((resolve, reject) => {
                socket().emit('set_results_visibility', liveVotingId, resultsVisible, resolve);
            }),
        fetchCurrentResults: (liveVotingId) =>
            new Promise((resolve, reject) => {
                socket().emit('get_current_results', liveVotingId, resolve);
            }),
        fetchQuestionsList: (liveVotingId) => new Promise((resolve, reject) => socket().emit('get_questions_list', liveVotingId, resolve)),
        /**
         * sendAnswer
         *
         * if question.answerType == 'text', optionId should be null and optionValue contain the text of the user answer
         * other type of questions, optionValue should be left empty and optionId should match one of the available options (optionId)
         */
        sendAnswer: (liveVotingId, questionId, optionId = null, optionValue = null) =>
            new Promise((resolve, reject) =>
                socket().emit('set_answer', liveVotingId, questionId, optionId, optionValue, (response) => {
                    if (response == 'success') {
                        resolve();
                    } else {
                        reject(response);
                    }
                })
            ),
        sendSettings: (liveVotingId, settings) =>
            new Promise((resolve, reject) => {
                socket().emit('set_room_settings', liveVotingId, settings, resolve);
            }),
        fetchSettings: (liveVotingId) =>
            new Promise((resolve, reject) => {
                socket().emit('get_room_settings', liveVotingId, resolve);
            }),
        fetchPreviousAnswer: (liveVotingId, questionId) =>
            new Promise((resolve, reject) => {
                socket().emit('get_answer', liveVotingId, questionId, resolve);
            }),
        removeQuestionAnswers: (liveVotingId, questionId) =>
            new Promise((resolve, reject) => {
                socket().emit('delete_answers', liveVotingId, questionId, resolve);
            }),
        removeLivevotingAnswers: (liveVotingId) =>
            new Promise((resolve, reject) => {
                socket().emit('reset_survey_answers', liveVotingId, resolve);
            }),
        startVotingSession: (liveVotingId) => {
            return new Promise((resolve, reject) => {
                socket().emit('start_session', liveVotingId, resolve);
            });
        },
        setAnswerPosition: (liveVotingId, position) =>
            new Promise((resolve, reject) =>
                socket().emit('open_question_set_new_position', liveVotingId, position, (savedPosition) => {
                    if (!isUndefined(savedPosition)) {
                        return resolve(savedPosition);
                    } else {
                        reject();
                    }
                })
            ),
        changeAnswerPosition: (livevotingId, change) =>
            new Promise((resolve, reject) => {
                socket().emit('open_question_set_relative_position', livevotingId, change, (savedPosition) => {
                    if (!isUndefined(savedPosition)) {
                        return resolve(savedPosition);
                    } else {
                        reject();
                    }
                });
            }),
        setAnswerValueFavorite: (liveVotingId, answer_value, set_favorite, keep_position = false) =>
            new Promise((resolve, reject) => {
                socket().emit('set_open_question_answer_favorite', liveVotingId, answer_value, set_favorite, keep_position, resolve);
            }),
        setAnswerValueArchive: (liveVotingId, answer_value, set_archive) =>
            new Promise((resolve, reject) => {
                socket().emit('set_open_question_answer_archive', liveVotingId, answer_value, set_archive, resolve);
            }),
        setPreviewMode: (liveVotingId, state) =>
            new Promise((resolve, reject) => {
                socket().emit('set_room_preview', liveVotingId, state, resolve);
            }),
        setAnswerValueShow: (liveVotingId, answer_value, show) =>
            new Promise((resolve, reject) => {
                socket().emit('set_answer_value_show', liveVotingId, answer_value, show, resolve);
            }),
        setAnswerValueDelete: (liveVotingId, answer_value, deleteStatus) =>
            new Promise((resolve, reject) => {
                socket().emit('set_answer_value_delete', liveVotingId, answer_value, deleteStatus, resolve);
            }),
        sendPreviewData: (liveVotingId, optionArray) =>
            new Promise((resolve, reject) => {
                socket().emit('send_room_preview_data', liveVotingId, optionArray, resolve);
            }),
        stopVotingSession: (liveVotingId, saveAnswers) =>
            // TODO: update documentation
            new Promise((resolve, reject) => {
                socket().emit('stop_session', liveVotingId, saveAnswers, (saved_answer_count) => {
                    // //console.log(saved_answer_count);
                    if (!isNaN(saved_answer_count)) {
                        return resolve(JSON.parse(saved_answer_count));
                    } else {
                        reject();
                    }
                });
            }),
        //
        //
        //
        on: (eventId, callback) => {
            return socket().on(eventId, callback);
        },
        off: (eventId, callback) => {
            return socket().off(eventId, callback);
        },
        once: (eventId, callback) => {
            return socket().once(eventId, callback);
        },
        isConnected: () => {
            return socket().connected;
        },
    },
    //
    //
    //
    on: (eventId, callback) => {
        return socket().on(eventId, callback);
    },
    off: (eventId, callback) => {
        return socket().off(eventId, callback);
    },
    once: (eventId, callback) => {
        return socket().once(eventId, callback);
    },
    isConnected: () => {
        return socket().connected;
    },
    //
    //
    //
    fetchEventVotingSessions: function (eventId) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/livevoting',
            method: 'GET',
            json: true,
        }).then((response) => response.data);
    },

    /**
     * Submits one survey question to server
     * @param  {Question} survey    Survey question
     * @return {Promise}
     */
    saveEventVotingQuestion: function (livevotingId, survey) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(survey.event_id) + '/voting' + (livevotingId && survey.id ? '/' + encodeURIComponent(livevotingId) : ''),
            method: survey.id ? 'PUT' : 'POST',
            json: true,
            data: {
                eventId: survey.event_id,
                text: survey.text,
                answer_type: survey.answer_type,
                position: survey.position,
                description: survey.description,
                category: survey.category,
                answerOptions: survey.options,
                editable: survey.editable,
                livevotingId: survey.livevoting_id,
                enrichmentId: survey.id,
                url: survey.url,
                allow_multiple_text_answers: survey.allow_multiple_text_answers,
            },
        }).then((r) => r.data);
    },

    fetchEventVotingStyles: function (eventId, livevoting_id) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/votingstyle/' + encodeURIComponent(livevoting_id),
            method: 'GET',
            json: true,
        }).then((response) => response.data);
    },

    updateEventVotingStyles: function (styles, eventId, livevoting_id) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/votingstyle/' + encodeURIComponent(livevoting_id),
            method: 'PUT',
            json: true,
            data: {
                styles: styles,
                eventId: eventId,
            },
        }).then((response) => {
            //console.log(response);
        });
    },

    deleteEventVotingQuestions: function (eventId, enrichmentId) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/voting/' + encodeURIComponent(enrichmentId),
            method: 'DELETE',
            json: true,
        }).then((response) => {
            //console.log(response);
        });
    },

    updateQuestionPosition: function (eventId, livevoting_id, position, enrichmentId) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/voting/' + encodeURIComponent(livevoting_id),
            method: 'PUT',
            json: true,
            data: {
                position: position,
                enrichmentId: enrichmentId,
            },
        }).then((response) => {
            //console.log(response);
        });
    },

    fetchVoteCountFromQuestion: function (livevotingId, enrichmentId) {
        return getAxiosInstance({
            url: '/livevoting/' + encodeURIComponent(livevotingId) + '/vote-count/' + encodeURIComponent(enrichmentId),
            method: 'GET',
            json: true,
        })
            .then((response) => {
                return isNaN(response.data) ? 'failed' : response.data;
            })
            .catch((err) => 'failed');
    },

    deleteVoteCountFromQuestion: function (livevotingId, enrichmentId) {
        return getAxiosInstance({
            url: '/livevoting/' + encodeURIComponent(livevotingId) + '/vote-count/' + encodeURIComponent(enrichmentId),
            method: 'DELETE',
            json: true,
        }).then((response) => response.data);
    },
});
